import type { QuestionKind } from "@talenthop/shared";
import {
  DEFAULT_INTERVIEW_LANGUAGE,
  INTERVIEW_LANGUAGES,
  SPOKEN_ACKS_BY_LANGUAGE,
  interviewLanguageOf,
  isInterviewLanguageCode,
  type InterviewLanguageCode,
} from "@talenthop/shared";
import type { InviteLanguage } from "./CandidateApp.js";

/**
 * The language-dependent decisions of the interview screen, kept PURE so they can be pinned under
 * the node-only web vitest project (no DOM, no React renderer — see apps/web/vitest.config.ts).
 * InterviewStep.tsx only executes these; it never decides.
 *
 * Every function here degrades to English rather than throwing: a session row written by a build
 * that knows more languages than this one, or a payload from an API that predates WS4, must land
 * on exactly today's behaviour (interviewLanguageOf is the one coercion point).
 */

/**
 * The languages the consent step may actually offer: the invite's list, narrowed to the codes
 * THIS build has consent text for, English always first, duplicates dropped. A code this build
 * does not know (a newer API) is dropped rather than shown — offering a language whose consent
 * summary we cannot render would be asking for consent to a notice the candidate never saw.
 * English is put back if the API omitted it: every requisition offers it, and it is what the
 * consent POST records when nothing else was chosen.
 */
export function pickableLanguages(offered: readonly InviteLanguage[] | undefined): InterviewLanguageCode[] {
  const known = (offered ?? []).map((l) => l.code).filter(isInterviewLanguageCode);
  const rest = known.filter((c) => c !== DEFAULT_INTERVIEW_LANGUAGE);
  return [DEFAULT_INTERVIEW_LANGUAGE, ...Array.from(new Set(rest))];
}

/** The slice of a state/answer question these helpers need — the full StateQuestion carries more. */
export interface SpeakableQuestion {
  kind: QuestionKind;
  /** Always English: the judge, the report and the plan compare on this one. */
  prompt: string;
  /** What Asha actually speaks — the API's rendering of `prompt` into the session language. Null or
   *  absent means "speak the English" — either the session is English or rendering failed and the
   *  API chose English over silence (migration 0078). */
  promptLocalized?: string | null;
}

/**
 * What Asha says out loud — and what the screen shows — for a given question.
 *
 * The localized rendering wins whenever the server sent one; the two must never diverge, because
 * a candidate who reads one sentence while hearing another has been given two questions. The
 * critique fallback below is the one exception to "speak the prompt as written": a code_reading
 * row that somehow has no prompt of its own must not leave the candidate in silence in front of a
 * snippet. That fallback stays English — it is a defect path, not a product surface, and a
 * localized row would have carried a localized prompt to begin with.
 */
export function spokenFor(q: SpeakableQuestion): string {
  const localized = q.promptLocalized?.trim();
  if (localized) return localized;
  if (q.kind === "code_reading" && !q.prompt.trim())
    return "Read this snippet and explain what it does in your own words — and tell me if you spot a bug.";
  return q.prompt;
}

/**
 * The receipt Asha speaks before every question after the first, in the session's language.
 * Rotated by seq exactly as the English ACKS always were, so an English interview is byte-for-
 * byte unchanged. Never characterising the answer — SPOKEN_ACKS_BY_LANGUAGE explains why.
 */
export function ackFor(language: unknown, seq: number): string {
  const acks = SPOKEN_ACKS_BY_LANGUAGE[interviewLanguageOf(language)];
  return acks[((seq % acks.length) + acks.length) % acks.length]!;
}

/** The BCP-47 tag the Web Speech fallback recogniser and utterance are told to expect. */
export function webSpeechLangFor(language: unknown): string {
  return INTERVIEW_LANGUAGES[interviewLanguageOf(language)].webSpeechLang;
}

/** Names that strongly indicate a female voice across common OS / browser voice sets. */
const FEMALE_VOICE_RE =
  /female|samantha|victoria|karen|moira|tessa|fiona|veena|raveena|zira|heera|neerja|susan|linda|joanna|salli|kendra|aria|jenny|amy|emma|nicole|serena|asha|priya|ananya|pallavi|swara|shruti|sapna|sobhana|aarohi|dhwani|tanishaa/i;

/** The two fields of SpeechSynthesisVoice the picker reads — typed structurally so the picker can
 *  be pinned with plain objects. */
export interface VoiceLike {
  name: string;
  lang: string;
}

/**
 * Pick ONE stable Asha voice for the Web Speech fallback.
 *
 * English keeps the original preference chain (a female en-IN voice, then any female English
 * voice, then any en-IN, then any English, then whatever the device has) — an English interview
 * must sound exactly as it did before WS4. Every other language walks the same chain for ITS tag
 * (exact `ta-IN` before the bare `ta` prefix, female first) and then stops: it never falls
 * through to an English voice, because an English engine handed Tamil script either spells the
 * characters out or stays silent, and silence is the honest outcome — the caller leaves the
 * utterance's `lang` set so the engine may still find a default of its own, and the on-screen
 * caption carries the question either way (the speech watchdog hands the turn back).
 */
export function pickAshaVoice<V extends VoiceLike>(voices: readonly V[], language: unknown): V | null {
  if (voices.length === 0) return null;
  const code: InterviewLanguageCode = interviewLanguageOf(language);
  const tag = INTERVIEW_LANGUAGES[code].webSpeechLang.toLowerCase(); // "ta-in"
  const prefix = tag.split("-")[0] ?? tag; // "ta"
  // English keeps the original predicates verbatim (no underscore normalisation) so the pre-WS4
  // pin is byte-for-byte; the normalisation exists for engines that report "ta_IN" for Indic voices.
  const norm = (l: string) => (code === DEFAULT_INTERVIEW_LANGUAGE ? l.toLowerCase() : l.toLowerCase().replace("_", "-"));
  const exact = (v: V) => norm(v.lang).startsWith(tag);
  const loose = (v: V) => norm(v.lang).startsWith(prefix);
  const fem = (v: V) => FEMALE_VOICE_RE.test(v.name);
  const best =
    voices.find((v) => exact(v) && fem(v)) ??
    voices.find((v) => loose(v) && fem(v)) ??
    voices.find(exact) ??
    voices.find(loose) ??
    null;
  if (best) return best;
  return code === DEFAULT_INTERVIEW_LANGUAGE ? (voices[0] ?? null) : null;
}
